/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import * as Yup from "yup";
import PrimaryButton from '../../../../Forms/PrimaryButton';

const route = 'resources/content/admin/affinity-groups';
const basePath = '/content-feature/affinitygroups';

const rootCss = css`
    max-width: 100%;

    .form-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        min-height: 80vh;
        
        .step-box {
            flex-grow: 1;
            min-height: 300px;
        }

        .nav-btn-box {
            margin-top: auto; 
            width: 30%;
            margin-left: auto;
            margin-right: auto;
        }

        .ms-Button {
            align-self: center;
        }
        .ms-Button--primary:not(.is-disabled) {
            height: 36px;
            .ms-Button-flexContainer {
                span {
                    color: #fff;
                }
            }
        }
        .save-btn-container {
            display: flex;
            margin-top: .25rem !important;
        }
    }

    .max-800 {
        max-width: 800px;
    }
    
    .preview-image {
        margin-bottom: 20px;
    }

    .preview-upload-help {
        margin: 10px 0 30px 0;
        font-size: 14px;
        color: var(--Z8);
    }
`;

const ISettingsSchema = Yup.object({
    affinityGroupSettingId: Yup.number().default(0),
    slug: Yup.string().required('Slug is required'),
    contagId: Yup.number().nullable(),
    homeHeroHeader: Yup.string().required('Home Hero Header is required'),
    homeHeroSubheader: Yup.string().nullable(),
    homeHeroImage: Yup.string().required('Home Hero Image is required'),
    homeHeroButtonUrl: Yup.string().nullable(),
    homeHeroButtonText: Yup.string().nullable(),
    featuredHeader: Yup.string().nullable(),
    featuredSubheader: Yup.string().nullable(),
    featuredVideoHeader: Yup.string().nullable(),
    featuredVideoSubheader: Yup.string().nullable(),
    featureBlockImage1: Yup.string().nullable(),
    featureBlockImage2: Yup.string().nullable(),
    featureBlockImage3: Yup.string().nullable(),
    featureBlockHeading1: Yup.string().nullable(),
    featureBlockHeading2: Yup.string().nullable(),
    featureBlockHeading3: Yup.string().nullable(),
    featureBlockBody1: Yup.string().nullable(),
    featureBlockBody2: Yup.string().nullable(),
    featureBlockBody3: Yup.string().nullable(),
    featureBlockUrl1: Yup.string().nullable(),
    featureBlockUrl2: Yup.string().nullable(),
    featureBlockUrl3: Yup.string().nullable(),
    featureBlockLinkText1: Yup.string().nullable(),
    featureBlockLinkText2: Yup.string().nullable(),
    featureBlockLinkText3: Yup.string().nullable(),
    primaryContentTagId: Yup.number().nullable(),
    primaryContentSlug: Yup.string().nullable(),
    primaryContentHeader: Yup.string().nullable(),
    primaryContentSeeMore: Yup.boolean().nullable(),
    secondaryContentTagId: Yup.number().nullable(),
    secondaryContentSlug: Yup.string().nullable(),
    secondaryContentHeader: Yup.string().nullable(),
    secondaryContentSeeMore: Yup.boolean().nullable(),
    courseHeader: Yup.string().nullable(),
    courseSeeMore: Yup.boolean().nullable(),
    toolHeader: Yup.string().nullable(),
    toolSeeMore: Yup.boolean().nullable(),
});

const validationSchema = Yup.array().of(
    Yup.object({
        settings: Yup.array().of(ISettingsSchema).min(1, 'At least one settings object is required'),
        primaryContent: Yup.array(),
        courses: Yup.array(),
        tools: Yup.array(),
        secondaryContent: Yup.array(),
        featuredVideo: Yup.array(),
        featuredVideoList: Yup.array(),
        dataOptions: Yup.array(),
        enabledCobrands: Yup.array(),
    })
);

function mapInitialValues(data) {
        return data && Object.keys(data).length 
            ? { ...data,
                tagOrPrimaryContent: data.settings.primaryContentTagID ? 'tag' : data.primaryContent.length > 0 ? 'content' : null,
                tagOrSecondaryContent: data.settings.secondaryContentTagID ? 'tag' : data.secondaryContent.length > 0 ? 'content' : null,
                primaryContent: data.settings.primaryContentTagID ? [] : data.primaryContent || [],
                secondaryContent: data.settings.secondaryContentTagID ? [] : data.secondaryContent || [],
             } 
            : 
        {
            settings: {
                    affinityGroupSettingId: 0,
                    slug: '',
                    conTagID: null,
                    homeHeroHeader: '',
                    homeHeroSubheader: '',
                    homeHeroImage: '',
                    homeHeroImagePath: '',
                    homeHeroButtonUrl: '',
                    homeHeroButtonText: '',
                    featuredHeader: '',
                    featuredSubheader: '',
                    featuredVideoHeader: '',
                    featuredVideoSubheader: '',
                    featureBlockImage1: '',
                    featureBlockImage2: '',
                    featureBlockImage3: '',
                    featureBlockHeading1: '',
                    featureBlockHeading2: '',
                    featureBlockHeading3: '',
                    featureBlockBody1: '',
                    featureBlockBody2: '',
                    featureBlockBody3: '',
                    featureBlockUrl1: '',
                    featureBlockUrl2: '',
                    featureBlockUrl3: '',
                    featureBlockLinkText1: '',
                    featureBlockLinkText2: '',
                    featureBlockLinkText3: '',
                    primaryContentTagID: null,
                    primaryContentSlug: '',
                    primaryContentHeader: '',
                    primaryContentSeeMore: false,
                    secondaryContentTagID: null,
                    secondaryContentSlug: '',
                    secondaryContentHeader: '',
                    secondaryContentSeeMore: false,
                    courseHeader: '',
                    courseSeeMore: false,
                    toolHeader: '',
                    toolSeeMore: false,
                },
            primaryContent: [],
            courses: [],
            tools: [],
            secondaryContent: [],
            featuredVideo: null,
            featuredVideoList: [],
            dataOptions: {content: [], topics: [], videos: [], tools: [], courses: []},
            enabledCobrands: [],
            tagOrPrimaryContent: null,
            tagOrSecondaryContent: null,
        };
}

const successMsg = "Affinity Groups Updated.";

const setErrorMsg = (show, errors) => (
    show && Object.keys(errors).length > 0
        ? `Errors in: ${Object.keys(errors).join(', ')}. Check all required * fields.`
        : "Errors in form, check all required fields and try again."
);

const basicInfoFields = [
    { name: "settings.homeHeroHeader", label: "Hero Header", required: true },
    { name: "settings.homeHeroSubheader", label: "Hero Subheader" },
    { name: "settings.homeHeroButtonUrl", label: "Hero Button Url" },
    { name: "settings.homeHeroButtonText", label: "Hero Button Text" },
];

const featureInfoFields = [
    { name: "settings.featuredHeader", label: "Featured Header" },
    { name: "settings.featuredSubheader", label: "Featured Subheader" },
];

const featureVideoFields = [
    { name: "settings.featuredVideoHeader", label: "Featured Video Header" },
    { name: "settings.featuredVideoSubheader", label: "Featured Video Subheader" },
];

const featureBlockFields = (x) => [
    { name: `settings.featureBlockHeading${x}`, label: `Block ${x} Heading` },
    { name: `settings.featureBlockBody${x}`, label: `Block ${x} Body` },
    { name: `settings.featureBlockUrl${x}`, label: `Block ${x} Url` },
    { name: `settings.featureBlockLinkText${x}`, label: `Block ${x} LinkText` },
];

const resolveMediaUrl = (inputUrl, path) => {
    const url = inputUrl || '';
    const isFullUrl = url.startsWith('http://') || url.startsWith('https://');
    return isFullUrl ? url : url !== '' ? `https://cdn.igrad.com/${path}${url}` : null;
};

const removeBaseUrl = (inputUrl) => {
    if (!inputUrl) return null;

    const baseUrl = 'https://cdn.igrad.com/';
    return inputUrl.startsWith(baseUrl)
        ? inputUrl.substring(baseUrl.length)
        : inputUrl;
};


const getHeroImgOptions = (values) => {
    const path = values?.settings?.homeHeroImage ?? '';
    const relativePath = removeBaseUrl(path);

    return {
        name: 'settings.homeHeroImage',
        label: 'Header Image',
        helpMsg: "* Upload a new image or select one from our library.",
        previewUrl: resolveMediaUrl(relativePath, ''), 
        width: 168,
        height: 168,
        gallery: {
            name: "settings.homeHeroImage",
            blobUrl: "https://igradcdnproduction.blob.core.windows.net",
            containerName: "images",
            folderName: "affinity/",
            imageUrl: "https://cdn.igrad.com/images/",
            heading: "Select an image or upload a new one",
            btnText: "Select Image"
        },
    }
};


const getBlockImgOptions = (num, previewUrl) => {
    const path = previewUrl ?? '';
    const relativePath = removeBaseUrl(path);

    return {
        name: `settings.featureBlockImage${num}`,
        label: `Featured Block ${num} Image`,
        helpMsg: "* Upload a new image or select one from our library.",
        previewUrl: resolveMediaUrl(relativePath, ''),
        width: 168,
        height: 168,
        gallery: {
            name: `settings.featureBlockImage${num}`,
            blobUrl: "https://igradcdnproduction.blob.core.windows.net",
            containerName: "images",
            folderName: "affinity/",
            imageUrl: "https://cdn.igrad.com/images/",
            heading: "Select an image or upload a new one",
            btnText: "Select Image"
        },
    }
};

const tagOrPrimaryContentOptions = [
    { key: 'tag', text: "Tag" },
    { key: 'content', text: "Content" },
];

const tagOrSecondaryContentOptions = [
    { key: 'tag', text: "Tag" },
    { key: 'content', text: "Content" },
];

const unsavedMsg = 'You have unsaved changes, are you sure you want to leave?';

const SaveButton = ({ handleSave, dirty, submitting }) => {
    return (
        <div className="save-btn-container">
            <PrimaryButton
                onClick={handleSave}
                text="Save"
                disabled={!dirty || submitting}
            />
        </div>
    );
};


export {
    route,
    basePath,
    rootCss,
    validationSchema,
    mapInitialValues,
    successMsg,
    setErrorMsg,
    basicInfoFields,
    featureInfoFields, 
    featureVideoFields, 
    featureBlockFields,
    removeBaseUrl,
    getHeroImgOptions,
    getBlockImgOptions,
    tagOrPrimaryContentOptions,
    tagOrSecondaryContentOptions,
    unsavedMsg,
    SaveButton,
};
